import React, { useCallback, useEffect, useRef, useState } from "react";

type Props = {
  id: string;
  value?: string;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  maxLength?: number;
  type?: React.InputHTMLAttributes<HTMLInputElement>["type"];
  style?: React.CSSProperties;
  onCommit: (valueText: string) => void;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
};

export default function DebouncedTextInput({
  id,
  value = "",
  className,
  disabled,
  placeholder,
  maxLength,
  type = "text",
  style,
  onCommit,
  onFocus,
}: Props) {
  const [localValue, setLocalValue] = useState<string>(value ?? "");
  const timerRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if ((value ?? "") !== localValue) {
      setLocalValue(value ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const scheduleCommit = useCallback(
    (next: string) => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
      }
      timerRef.current = window.setTimeout(() => {
        onCommit(next ?? "");
      }, 200);
    },
    [onCommit]
  );

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      const next = e.target.value ?? "";
      setLocalValue(next);
      scheduleCommit(next);
    },
    [scheduleCommit]
  );

  const handleBlur = useCallback(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
    onCommit(localValue ?? "");
  }, [localValue, onCommit]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
      }
    };
  }, []);

  return (
    <input
      id={id}
      type={type}
      value={localValue}
      placeholder={placeholder}
      maxLength={maxLength}
      className={className}
      disabled={disabled}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={onFocus}
      style={style}
    />
  );
}
